import { useEffect, useState } from "react";
import { Building2, Loader2, Mail, FolderKanban, AlertCircle, Coins, RefreshCw } from "lucide-react";
import { api } from "../lib/api";
import { fmtRelative, templatesMeta } from "../lib/format";
import { toast } from "sonner";

export default function ClientProjects() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [snapBusy, setSnapBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/client-projects")
      .then((r) => setRows(r.data.projects || []))
      .catch((e) => setError(e?.response?.data?.detail || "Failed to load clients"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const refreshSnapshot = async () => {
    setSnapBusy(true);
    try {
      const r = await api.post("/admin/snapshot-credits-now");
      toast.success(`Snapshot refreshed — ${r.data?.orgs_recorded ?? 0} orgs, ${r.data?.projects_recorded ?? 0} projects`);
      load();
    } catch {
      toast.error("Could not refresh credit snapshot");
    } finally {
      setSnapBusy(false);
    }
  };

  // Group projects by org name for clearer scanning
  const byOrg = rows.reduce((acc, p) => {
    const key = p.org_name || p.org_id || "Unknown";
    if (!acc[key]) {
      acc[key] = {
        name: key,
        plan: p.org_plan,
        org_credits_balance: p.org_credits_balance,
        org_credits_starting: p.org_credits_starting,
        org_credits_used_24h: p.org_credits_used_24h ?? 0,
        items: [],
      };
    }
    acc[key].items.push(p);
    return acc;
  }, {});

  return (
    <div className="p-8 lg:p-12" data-testid="client-projects-page">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="type-eyebrow text-[#64748B]">Super Admin</div>
          <h1 className="type-h1 text-[#0F1F3D] mt-3">Clients</h1>
          <p className="type-body text-[#475569] mt-3 prose-measure">
            Every client organisation and their projects — segregated from your own ORL projects.
            Credits-used columns are sampled daily at noon (server-local) and cover the last 24-hour window.
          </p>
        </div>
        <button
          onClick={refreshSnapshot}
          disabled={snapBusy}
          data-testid="clients-refresh-snapshot"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-[#0F1F3D] text-[#0F1F3D] hover:bg-[#F1F5F9] text-sm font-medium disabled:opacity-60"
        >
          <RefreshCw className={`h-4 w-4 ${snapBusy ? "animate-spin" : ""}`} />
          {snapBusy ? "Refreshing…" : "Refresh credit snapshot"}
        </button>
      </div>

      {loading && (
        <div className="mt-10 bg-white border border-[#E2E8F0] rounded-lg p-12 text-center type-body text-[#64748B]">
          <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" /> Loading clients…
        </div>
      )}

      {!loading && error && (
        <div className="mt-10 bg-[#FEF2F2] border border-[#FECACA] rounded-lg p-6 flex items-start gap-3" data-testid="client-projects-error">
          <AlertCircle className="h-5 w-5 text-[#B91C1C] mt-0.5 shrink-0" />
          <div className="text-sm text-[#B91C1C]">{error}</div>
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div className="mt-10 bg-white border border-[#E2E8F0] rounded-lg p-16 text-center" data-testid="client-projects-empty">
          <FolderKanban className="h-8 w-8 text-[#94A3B8] mx-auto mb-4" />
          <div className="type-h3 text-[#0F1F3D]">No client projects yet</div>
          <div className="type-body text-[#64748B] mt-3 prose-measure mx-auto">
            When other organisations create projects on LaunchRadar, they will appear here.
            Your ORL projects continue to live under the standard Projects tab.
          </div>
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <div className="mt-10 space-y-8">
          {Object.values(byOrg).map((grp) => (
            <div
              key={grp.name}
              className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden"
              data-testid={`client-org-group-${grp.name.replace(/\s+/g, "-")}`}
            >
              <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between bg-[#F8FAFC] flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-md bg-[#0F1F3D]/8 flex items-center justify-center">
                    <Building2 className="h-4 w-4 text-[#0F1F3D]" />
                  </div>
                  <div>
                    <div className="type-eyebrow text-[#64748B]">Organisation</div>
                    <div className="type-h4 text-[#0F1F3D]">{grp.name}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  {grp.plan && (
                    <span className="px-2.5 py-1 rounded-full bg-[#EEF2FF] text-[#3730A3] type-eyebrow font-semibold">
                      {grp.plan}
                    </span>
                  )}
                  <span
                    title="Credits used in the last 24h (noon-to-noon snapshot)"
                    data-testid={`org-credits-24h-${grp.name.replace(/\s+/g, "-")}`}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FEF3C7] border border-[#FDE68A] text-[#92400E] type-eyebrow font-bold"
                  >
                    <Coins className="h-3 w-3" />
                    {grp.org_credits_used_24h} used / 24h
                  </span>
                  {typeof grp.org_credits_balance === "number" && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#ECFDF5] border border-[#A7F3D0] text-[#065F46] type-eyebrow font-bold">
                      <Coins className="h-3 w-3" />
                      {grp.org_credits_balance.toLocaleString()} balance
                    </span>
                  )}
                  <span className="type-caption text-[#64748B]">
                    {grp.items.length} project{grp.items.length === 1 ? "" : "s"}
                  </span>
                </div>
              </div>

              <table className="w-full">
                <thead className="bg-[#F8F9FA] type-eyebrow text-[#64748B]">
                  <tr>
                    <th className="text-left px-6 py-3">Project</th>
                    <th className="text-left px-6 py-3">Templates</th>
                    <th className="text-left px-6 py-3">Owner</th>
                    <th className="text-right px-6 py-3">Results</th>
                    <th className="text-right px-6 py-3">Credits / 24h</th>
                    <th className="text-right px-6 py-3">Last run</th>
                    <th className="text-right px-6 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {grp.items.map((p) => {
                    const tms = templatesMeta(p.template_ids || []);
                    return (
                      <tr
                        key={p.id}
                        data-testid={`client-project-row-${p.id}`}
                        className="border-t border-[#E2E8F0]"
                      >
                        <td className="px-6 py-4 align-top">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="type-h4 text-[#0F1F3D]">{p.name}</div>
                            {p.new_results_count > 0 && (
                              <span
                                title={`${p.new_results_count} new in the last 24h`}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#ECFDF5] border border-[#A7F3D0] text-[#047857] text-[11px] font-bold leading-none"
                              >
                                <span className="h-1.5 w-1.5 rounded-full bg-[#10B981] animate-pulse" />
                                {p.new_results_count} new
                              </span>
                            )}
                          </div>
                          {(p.sectors || []).length > 0 && (
                            <div className="type-caption text-[#64748B] mt-1">
                              {(p.sectors || []).join(" · ")}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 align-top">
                          <div className="flex flex-wrap gap-1.5">
                            {tms.map((tm) => (
                              <span
                                key={tm.id || tm.label}
                                className="px-2 py-1 rounded-md type-eyebrow"
                                style={{ background: tm.color + "15", color: tm.color }}
                              >
                                {tm.label}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4 align-top">
                          <div className="text-sm text-[#0F1F3D]">{p.creator_name || "—"}</div>
                          {p.creator_email && (
                            <a
                              href={`mailto:${p.creator_email}`}
                              className="inline-flex items-center gap-1 type-caption text-[#475569] hover:text-[#0F1F3D] mt-0.5"
                            >
                              <Mail className="h-3 w-3" /> {p.creator_email}
                            </a>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right font-mono-tab text-sm text-[#0F1F3D] align-top">
                          {p.results_count || 0}
                        </td>
                        <td
                          className="px-6 py-4 text-right font-mono-tab text-sm align-top"
                          data-testid={`project-credits-24h-${p.id}`}
                        >
                          <span className={p.credits_used_24h > 0 ? "text-[#92400E]" : "text-[#94A3B8]"}>
                            {p.credits_used_24h || 0}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right type-caption text-[#64748B] align-top">
                          {fmtRelative(p.last_run_at)}
                        </td>
                        <td className="px-6 py-4 text-right align-top">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold leading-none ${
                              p.is_active
                                ? "bg-[#ECFDF5] text-[#047857]"
                                : "bg-[#FEF3C7] text-[#92400E]"
                            }`}
                          >
                            <span
                              className={`h-1.5 w-1.5 rounded-full ${
                                p.is_active ? "bg-[#10B981]" : "bg-[#F59E0B]"
                              }`}
                            />
                            {p.is_active ? "Active" : "Paused"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
